// Lógica pura del módulo Ficha Técnica. Sin React ni I/O — testeable en aislado.
import type { BOMCompleto, ResumenBloques, BloqueResumen } from '@/types/bomCaptura'

/**
 * Ejecuta `intento` reintentando mientras el error sea reintentable (p.ej. error
 * de red) y no se supere `maxReintentos`. Propaga el último error si se agotan o
 * el error no es reintentable. Usado por useImageUpload.
 */
export async function withNetworkRetry<T>(
  intento: (n: number) => Promise<T>,
  esReintentable: (e: unknown) => boolean,
  maxReintentos = 2,
): Promise<T> {
  for (let n = 0; ; n++) {
    try {
      return await intento(n)
    } catch (e) {
      if (!(esReintentable(e) && n < maxReintentos)) throw e
    }
  }
}

/**
 * Evalúa los 6 bloques fijos de la ficha técnica (RF-04). El botón "Generar" se
 * habilita sólo cuando `todoListo` es true.
 *
 * B1 Cabecera        → siempre (datos de Dataverse)
 * B2 Producto final  → la cabecera tiene imagen de conjunto
 * B3 Características  → ≥ 1 captura tipo 'caracteristica'
 * B4 Tabla compon.   → hay líneas de componente
 * B5 Galería         → todas las líneas tienen imagen
 * B6 Proceso         → ≥ 1 captura tipo 'manipulacion'
 */
export function evaluarBloques(bom: BOMCompleto): ResumenBloques {
  const { cabecera, lineas, capturas } = bom
  const carac = capturas.filter((c) => c.tipo === 'caracteristica').length
  const manip = capturas.filter((c) => c.tipo === 'manipulacion').length
  const total = lineas.length
  const sinImagen = lineas.filter((l) => !l.tieneImagen).length

  const bloques: BloqueResumen[] = [
    { numero: 1, clave: 'cabecera', estado: 'listo', detalle: 'desde Dataverse' },
    {
      numero: 2,
      clave: 'productoFinal',
      estado: cabecera.productoFinalTieneImagen ? 'listo' : 'incompleto',
      detalle: cabecera.productoFinalTieneImagen ? 'imagen de conjunto' : 'sin imagen de conjunto',
    },
    {
      numero: 3,
      clave: 'caracteristicas',
      estado: carac >= 1 ? 'listo' : 'incompleto',
      detalle: `${carac} características`,
    },
    {
      numero: 4,
      clave: 'tablaComponentes',
      estado: total > 0 ? 'listo' : 'incompleto',
      detalle: `${total} componentes`,
    },
    {
      numero: 5,
      clave: 'galeria',
      estado: total > 0 && sinImagen === 0 ? 'listo' : 'incompleto',
      detalle: sinImagen === 0 ? `${total} con imagen` : `${sinImagen} sin imagen`,
    },
    {
      numero: 6,
      clave: 'proceso',
      estado: manip >= 1 ? 'listo' : 'incompleto',
      detalle: `${manip} pasos`,
    },
  ]

  return { bloques, todoListo: bloques.every((b) => b.estado === 'listo') }
}
